/**
 * Core protocol types for Bifrost RPC Framework
 */

// Message types
export type RpcKind = "call" | "notify" | "response";

export interface V2Hello {
  kind: "hello";
  documentNonce: string;
  handshakeAttempt: string;
  protocols: number[];
  features: string[];
  webToNativeTransports: string[];
  nativeToWebTransports: string[];
  representations: string[];
  limits: {
    maxDirectFrameBytes: number;
    admissionWindow: number;
    controlPriorityWindow: number;
    maxMultipartBytes?: number;
    multipartChunkBytes?: number;
    maxActiveBulkTransfers?: number;
  };
}

export interface V2SessionGrant {
  kind: "session_grant";
  protocol: 2;
  sessionId: string;
  capability: string;
  features: string[];
  webToNativeTransport: string;
  nativeToWebTransport: string;
  limits: {
    maxDirectFrameBytes: number;
    admissionWindow: number;
    controlPriorityWindow: number;
    maxMultipartBytes?: number;
    multipartChunkBytes?: number;
    maxActiveBulkTransfers?: number;
  };
}

export interface V2SessionRejection {
  kind: "session_rejected";
  reason: string;
}

export interface V2ControlFrame {
  protocol: 2;
  sessionId: string;
  capability: string;
  kind:
    | "session_ready"
    | "session_ready_ack"
    | "call"
    | "notify"
    | "response"
    | "multipart_chunk";
  id?: string;
  service?: string;
  method?: string;
  payload?: unknown;
  ok?: boolean;
  result?: unknown;
  error?: unknown;
  ts?: number;
  chunk?: BifrostChunk;
  [optionalField: string]: unknown;
}

export interface V2BootstrapAdapter {
  hello(hello: V2Hello): Promise<V2SessionGrant | V2SessionRejection>;
  arm(receiver: (frame: string) => void): () => void;
  send(frame: string): "admitted" | "not_admitted" | void;
}

// Base envelope for all messages
export interface RpcEnvelope {
  id: string; // Always required for tracking
  kind: RpcKind;
  service?: string; // Required for kind="call"
  method?: string; // Required for kind="call"
  topic?: string; // Required for kind="notify"
  payload?: unknown; // Any JSON-serializable data
  expectAck?: boolean; // For notify, default false
  ts: number; // Unix timestamp in milliseconds
  headers?: Record<string, string>; // Request headers
  applicationCallId?: string;
  idempotencyKey?: string;
  deadlineAt?: number;
  timing?: {
    // Performance timing data
    startTime: number;
    endTime: number;
    duration: number;
  };
}

// Response structure
export interface RpcResponse {
  id: string; // Matches request id
  ok: boolean;
  result?: unknown; // Success result
  error?: RpcError; // Error details
  ts: number; // Response timestamp
  metadata?: Record<string, unknown>; // Response metadata
  timing?: {
    // Performance timing data
    startTime: number;
    endTime: number;
    duration: number;
  };
}

// Error structure
export interface RpcError {
  code: number; // Error code
  message: string; // Human-readable message
  data?: unknown; // Additional error context
}

// Service metadata
export interface ServiceDescriptor {
  name: string;
  methods: MethodDescriptor[];
  version?: string;
}

export interface MethodDescriptor {
  name: string;
  params?: ParamDescriptor[];
  returns?: string; // Return type description
  throws?: number[]; // Possible error codes
}

export interface ParamDescriptor {
  name: string;
  type: string; // "string" | "number" | "boolean" | "object" | "array"
  required: boolean;
  description?: string;
}

// Call options
export interface CallOptions {
  maxRetries?: number;
  timeout?: number;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

// Pending call tracking
export interface PendingCall {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
  envelope: RpcEnvelope;
}

// Interceptor interface
export interface Interceptor {
  name: string;
  order: number;
  intercept(
    direction: "inbound" | "outbound",
    message: RpcEnvelope | RpcResponse,
    next: () => Promise<any>,
  ): Promise<any>;
}

/**
 * How long `initialize()` waits for the native handshake when the caller names no
 * deadline of its own.
 *
 * Sized to the host's own page deadline, not to how long a handshake usually takes.
 * The native host abandons the page and reports a timeout at 15s; a shorter bridge
 * deadline leaves a window where the shim can still arrive, the web side has already
 * rejected, and native is still waiting — so the handshake never completes even
 * though nothing is broken. Anything longer is wasted: by then the failure has
 * already been reported and seen.
 */
export const DEFAULT_READY_TIMEOUT_MS = 15_000;

// Configuration interfaces
export interface BifrostConfig {
  readyTimeout?: number; // Default: DEFAULT_READY_TIMEOUT_MS
  maxInflightCalls?: number; // Default: 32
  /**
   * `retryConfig.retryableErrors` is ignored on this client's own call path: a
   * native error response means the call was admitted and may already have
   * changed state, so `BifrostWeb.call()` never replays it on the strength of
   * an error class, regardless of what this list contains. It still
   * configures a standalone `RetryManager` used directly by a caller.
   */
  retryConfig?: RetryConfig;
  enableDebug?: boolean; // Default: false in production
  interceptors?: Interceptor[]; // Default interceptors to register
  onReady?: () => void; // Ready callback
  onError?: (error: Error) => void;
  defaultCallOptions?: CallOptions; // Default options for all RPC calls (can be overridden at proxy or call level)
}

export interface RetryConfig {
  maxRetries?: number; // Default: 3
  retryDelay?: number; // Default: 1000ms
  retryBackoff?: number; // Default: 2 (exponential)
  retryableErrors?: number[]; // Error codes to retry
}

// Service context for handlers
export interface ServiceContext {
  // Future: can add context fields as needed
}

// Multipart chunk envelope for large message transport
export interface BifrostChunk {
  type: "bifrost_chunk";
  transferId: string;
  /** Correlates a chunked response before its payload has been reassembled. */
  responseId?: string;
  index: number;
  total: number;
  data: string;
}

/** Default 50 MiB payloads can need 401 chunks at UTF-8 scalar boundaries. */
export const MAX_CHUNKS_PER_TRANSFER = 401;
